import { z } from "zod";
import { smbManifestSchema, smbMoneySchema, smbJsonObjectSchema, smbTemplateOverridesSchema } from "./smb";
import { smbLpWarningSchema } from "./smb-lp-templates";

export const smbPackageInputSchema = z.object({
  slug: z.string().trim().min(1).max(160).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  name: z.string().trim().min(1).max(255),
  active: z.boolean(), isDefault: z.boolean(),
  manifestJson: smbManifestSchema, costCapUsd: smbMoneySchema,
  maxRevisions: z.number().int().min(0).max(10),
  humanReviewMode: z.literal("always"),
  qaPolicyJson: smbJsonObjectSchema.nullable(),
}).strict().refine(value => !value.isDefault || value.active, "The default package must be active");
export const smbPackageViewSchema = smbPackageInputSchema.innerType().extend({
  id: z.string(), pausedReason: z.string().nullable(),
}).strip();
export const smbPackageListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).max(10000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
}).strict();
export const smbCatalogQuerySchema = smbPackageListQuerySchema.extend({
  kind: z.enum(["static", "meme", "slideshow", "ugc"]).default("static"),
  q: z.string().trim().max(160).default(""),
  brandId: z.string().uuid().optional(),
  scope: z.enum(["default", "all-brands"]).default("default"),
  ids: z.string().max(5000).transform(value => value.split(","))
    .pipe(z.array(z.string().min(1).max(160)).min(1).max(30)).optional(),
}).refine(value => value.scope !== "all-brands" || (value.kind === "static" && !value.brandId), {
  message: "All-brand browsing is only available for the package static catalog without a target brand",
  path: ["scope"],
});
export const smbEligibilitySchema = z.object({
  id: z.string(), eligible: z.boolean(),
  warnings: z.array(z.union([z.enum(["unresolved", "archived", "non_static", "missing_example", "brand_restricted"]), smbLpWarningSchema])),
});
export const smbCatalogItemSchema = z.object({
  id: z.string(), name: z.string(), category: z.string().nullable(),
  thumbnail: z.string().nullable(), eligibility: smbEligibilitySchema,
  brandId: z.string().nullable().optional(),
  brandName: z.string().nullable().optional(),
});
export const smbCatalogResponseSchema = z.object({
  items: z.array(smbCatalogItemSchema), total: z.number(), page: z.number(), pageSize: z.number(),
});
export const smbPackageResponseSchema = z.object({
  package: smbPackageViewSchema,
  warnings: z.array(smbEligibilitySchema),
});
export const smbPackageListResponseSchema = z.object({
  packages: z.array(smbPackageViewSchema), total: z.number(), page: z.number(), pageSize: z.number(),
});
export const smbOrderTemplatesSchema = z.object({
  package: smbPackageViewSchema.nullable(), overrides: smbTemplateOverridesSchema,
  brandId: z.string().nullable(), baseManifest: smbManifestSchema.nullable(), editable: z.boolean(),
  manifest: smbManifestSchema.nullable(), warnings: z.array(smbEligibilitySchema),
});
export type SmbPackageInput = z.infer<typeof smbPackageInputSchema>;
export type SmbCatalogItem = z.infer<typeof smbCatalogItemSchema>;
export type SmbEligibility = z.infer<typeof smbEligibilitySchema>;
